import clsx from 'clsx'

import { DelimitedContent, Icon, Slash } from '@fv/client-components'
import { displayLocShipType, getLocAccessorials } from '@fv/client-core'
import type {
  Quote,
  ShipmentLocation,
  ShipType,
  SlimTrackDetail,
} from '@fv/client-types'
import { fvDate, fvDateUtc } from '@fv/dates'
import type { Workflow } from '@fv/models'

import { quoteFuncs } from '@/features/quote/quoteFuncs'
import { useTypeNames } from '@/hooks/settings'
import { getApplicableAccessorials } from '@/utils/locationFuncs'

import { DividedContent } from '../DividedContent'
import { ListItem } from '../List'
import { ShipperTooltip } from '../ShipperTooltip'

type StopListLocation = Pick<
  ShipmentLocation,
  | 'accessorials'
  | 'company'
  | 'refNums'
  | 'sequence'
  | 'stopDateType'
  | 'stopType'
  | 'type'
> & {
  city?: string
  postalCode?: string
  shipType?: ShipType | ''
  state?: string
  stopDate?: Date | string | null
}

type ExpandedDetailsProps = Pick<
  ShipmentLocation,
  'accessorials' | 'refNums'
> & {
  shipType?: ShipType | ''
}

const ExpandedDetails = ({
  accessorials,
  shipType,
  refNums,
}: ExpandedDetailsProps) => {
  const typeNames = useTypeNames()
  const shipTypeDisplay = displayLocShipType({ shipType, accessorials })
  const accessorialList = getLocAccessorials({ accessorials }, typeNames)
    ?.map(a =>
      a.key === 'schedule' && a.appointmentTime
        ? `${a.name} (${a.appointmentTime})`
        : a.name,
    )
    ?.join(' / ')

  return (
    <ListItem className="flex flex-nowrap items-start">
      <Icon className="text-fv-gray flex-none" icon="dot-circle" />
      <p className="mb-0">
        <DelimitedContent delimiter={<Slash />}>
          {!!refNums?.length && `Ref# ${refNums.map(n => n.value).join(' / ')}`}
          {shipTypeDisplay}
          {!!accessorialList?.length && accessorialList}
        </DelimitedContent>
      </p>
    </ListItem>
  )
}

type StopProps = {
  address: string
  date: string
  isDestination?: boolean
  isOrigin?: boolean
  label: string
}

const Stop = ({ address, date, label, isOrigin, isDestination }: StopProps) => (
  <DividedContent className="shipment-card__stop">
    <span className="shipment-card__stop-type">
      <Icon
        icon="calendar"
        className={clsx(
          {
            'text-fv-orange': isOrigin,
            'text-fv-green': isDestination,
            'text-fv-beer': !isOrigin && !isDestination,
          },
          'w-5',
        )}
      />
      <span>{label}</span>
    </span>
    <span className="shipment-card__stop-date">{date}</span>
    <p className="shipment-card__location mb-0 flex flex-nowrap items-start !overflow-hidden">
      <Icon
        icon="building"
        className="text-fv-gray-dark"
        transform="down-2.2"
      />
      <ShipperTooltip label={address} onlyOnOverflow>
        <span className="truncate">{address}</span>
      </ShipperTooltip>
    </p>
  </DividedContent>
)

type StopDetailsProps = {
  isDestination: boolean
  isOrigin: boolean
  location: StopListLocation
  pickupDate?: string
  quote?: Quote
  tracking?: SlimTrackDetail
  workflow: Workflow
}

const StopDetails = ({
  isDestination,
  isOrigin,
  location,
  pickupDate,
  quote,
  tracking,
}: StopDetailsProps) => {
  const {
    city,
    company,
    postalCode,
    sequence,
    state,
    stopDate,
    stopDateType,
    stopType,
  } = location || {}

  const {
    currentStatus,
    deliveryDateActual,
    deliveryDateEstimate,
    lastUpdatedDate,
  } = tracking || {}

  const address = `${city} ${state} ${postalCode}`
  let fullAddress = company ? `${company}, ${address}` : address

  if (!postalCode) {
    if (isOrigin) fullAddress = 'Origin'
    else if (isDestination) fullAddress = 'Destination'
    else fullAddress = `Stop ${sequence}`
  }

  if (stopType === 'pickup') {
    const label = `Pick up${stopDateType ? ` ${stopDateType}` : ''}`
    const date = formatDate(stopDate, true)

    return (
      <Stop
        address={fullAddress}
        date={date || 'NA'}
        label={label}
        isOrigin={isOrigin}
      />
    )
  }

  if (stopType === 'delivery') {
    const estimate =
      deliveryDateEstimate ||
      stopDate ||
      quoteFuncs.getEstimatedDeliveryDate(pickupDate, quote)?.format()

    if (
      isDestination &&
      currentStatus !== 'delivered' &&
      !estimate &&
      !stopDate
    ) {
      return (
        <Stop
          address={fullAddress}
          date="NA"
          label="Estimated"
          isDestination={isDestination}
        />
      )
    }

    const type = stopDateType ? ` ${stopDateType}` : ''
    let label = isDestination ? `Deliver${type}` : `Drop off${type}`
    let date =
      isDestination && estimate
        ? formatDate(estimate)
        : formatDate(stopDate, true)

    if (isDestination && estimate) label = 'Estimated'
    if (isDestination && currentStatus === 'delivered') {
      label = 'Delivered'
      date = formatDate(deliveryDateActual ?? lastUpdatedDate)
    }

    return (
      <Stop
        address={fullAddress}
        date={date || 'NA'}
        label={label}
        isDestination={isDestination}
      />
    )
  }

  const date = formatDate(stopDate, true)
  const label = `PU/DO${stopDateType ? ` ${stopDateType}` : ''}`

  return <Stop address={fullAddress} date={date || 'NA'} label={label} />
}

interface ExpandableStopProps extends StopDetailsProps {
  isExpanded?: boolean
}

const ExpandableStop = ({
  isDestination,
  isExpanded,
  isOrigin,
  location,
  pickupDate,
  quote,
  tracking,
  workflow,
}: ExpandableStopProps) => {
  const { refNums } = location
  const accessorials = getApplicableAccessorials(location, workflow)
  const isExpandable = !!accessorials?.length || !!refNums?.length

  return (
    <>
      <ListItem
        className="flex flex-nowrap items-start"
        dashed={isExpandable && isExpanded}
      >
        <span className="flex-1 !overflow-hidden leading-[1.4rem]">
          <StopDetails
            isDestination={isDestination}
            isOrigin={isOrigin}
            location={location}
            tracking={tracking}
            workflow={workflow}
            pickupDate={pickupDate}
            quote={quote}
          />
        </span>
      </ListItem>

      {isExpandable && isExpanded && (
        <ExpandedDetails
          accessorials={accessorials}
          shipType={location.shipType}
          refNums={refNums}
        />
      )}
    </>
  )
}

type StopListProps = {
  isExpanded?: boolean
  locations: StopListLocation[]
  tracking?: SlimTrackDetail
  workflow: Workflow
  quote?: Quote
}

export const StopList = ({
  isExpanded,
  locations,
  quote,
  tracking,
  workflow,
}: StopListProps) => {
  const pickupDate =
    tracking?.pickupDateActual ||
    locations.find(loc => loc.type === 'origin')?.stopDate?.toString()

  return (
    <>
      {locations.map(x => (
        <ExpandableStop
          isDestination={x.sequence === locations.length - 1}
          isExpanded={isExpanded}
          isOrigin={x.sequence === 0}
          key={x.sequence}
          location={x}
          tracking={tracking}
          workflow={workflow}
          pickupDate={pickupDate}
          quote={quote}
        />
      ))}
    </>
  )
}
function formatDate(date?: Date | string | null, utc?: boolean) {
  if (!date) return ''
  const day = utc ? fvDateUtc(date) : fvDate(date)
  return day.format('ddd MMM D, YYYY')
}
